import { useState, useEffect, useCallback } from 'react';
import { getGACAStatus, getGACAHealth, GACAStatusResponse, GACAHealthResponse } from '../services/gacaClient';

interface UseGACAStatusOptions {
  pollInterval?: number; // in milliseconds
  enabled?: boolean;
}

interface UseGACAStatusReturn {
  status: GACAStatusResponse | null;
  health: GACAHealthResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  lastUpdated: Date | null;
}

const DEFAULT_POLL_INTERVAL = 30000; // 30 seconds

export function useGACAStatus(options: UseGACAStatusOptions = {}): UseGACAStatusReturn {
  const {
    pollInterval = DEFAULT_POLL_INTERVAL,
    enabled = true,
  } = options;

  const [status, setStatus] = useState<GACAStatusResponse | null>(null);
  const [health, setHealth] = useState<GACAHealthResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!enabled) return;

    try {
      setIsLoading(true);
      setError(null);

      const [statusResult, healthResult] = await Promise.all([
        getGACAStatus(),
        getGACAHealth(),
      ]);

      setStatus(statusResult);
      setHealth(healthResult);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch G.A.C.A. status'));
      // Set degraded status on error
      setStatus({
        status: 'down',
        activeProviders: 0,
        totalProviders: 7,
        providers: [],
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  // Initial fetch
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Polling
  useEffect(() => {
    if (!enabled || pollInterval <= 0) return;

    const interval = setInterval(fetchStatus, pollInterval);
    return () => clearInterval(interval);
  }, [enabled, pollInterval, fetchStatus]);

  return {
    status,
    health,
    isLoading,
    error,
    refetch: fetchStatus,
    lastUpdated,
  };
}

// Derived helper hooks
export function useGACAActiveProviders(): number {
  const { status } = useGACAStatus({ pollInterval: 60000 }); // Poll less frequently
  return status?.activeProviders ?? 0;
}

export function useGACAIsHealthy(): boolean {
  const { status, health } = useGACAStatus({ pollInterval: 60000 });
  return status?.status === 'healthy' && health?.status === 'ok';
}

export default useGACAStatus;
